import { Button, Tooltip } from '@mantine/core'
import { IconArrowsMaximize, IconArrowsMinimize, IconX } from '@tabler/icons-react'
import { ReactNode } from 'react'
import { CHART_CONTENT_HEIGHT_VAR, CHART_CONTENT_TOP_VAR } from '../chartTopOffset'

/** Default (non-fullWidth) panel width — the chart header/pane offsets in PatientPage must match. */
export const PATIENT_SIDE_PANEL_WIDTH = 550

type PatientSidePanelProps = {
  open: boolean
  fullWidth: boolean
  title?: string
  onClose: () => void
  onToggleFullWidth: () => void
  children: ReactNode
}

export function PatientSidePanel({
  open,
  fullWidth,
  title,
  onClose,
  onToggleFullWidth,
  children,
}: PatientSidePanelProps) {
  if (!open) {
    return null
  }

  return (
    <aside
      style={{
        // Full width: start below the whole sticky stack (Shell header +
        // RecentPatientsBar + patient chart action bar) so the patient header —
        // name, Identity Verified badge, Resend Invitation — stays visible.
        // Docked: the action bar shrinks to the panel's left edge
        // (PatientPage's chartRightOffset), so the panel starts right under the
        // Shell header + RecentPatientsBar and fills the column beside it.
        top: fullWidth ? CHART_CONTENT_TOP_VAR : 'calc(64px + var(--recent-bar-height, 0px))',
        height: fullWidth
          ? CHART_CONTENT_HEIGHT_VAR
          : 'calc(100vh - 64px - var(--recent-bar-height, 0px))',
        width: fullWidth ? 'calc(100% - 280px)' : `${PATIENT_SIDE_PANEL_WIDTH}px`,
      }}
      // z-[60] keeps the panel above in-chart chrome; it never covers the
      // action bar (z-50) — docked mode offsets the bar, full width starts
      // below it.
      className="fixed right-0 z-[60] flex flex-col border-l border-l-[#EEEEEE] bg-[#FBFBFB]"
    >
      {/* Header travels WITH the panel (not the viewport), so fullscreen never
          hides the title nor collides the controls with the chart's Sync pill. */}
      <div className="flex items-center justify-between border-b border-b-[#EEEEEE] px-6 py-3">
        <p className="text-lg font-semibold">{title}</p>
        <div className="flex items-center gap-2">
          <Tooltip
            label={fullWidth ? 'Collapse panel' : 'Expand panel'}
            position="left"
            openDelay={200}
          >
            <Button
              variant="outline"
              className="!h-[24px] !w-[24px] !rounded-full !bg-[#F0F7FF] !p-0"
              onClick={onToggleFullWidth}
            >
              {fullWidth ? <IconArrowsMinimize width={15} /> : <IconArrowsMaximize width={15} />}
            </Button>
          </Tooltip>
          <Tooltip label="Close panel" position="left" openDelay={200}>
            <Button
              variant="outline"
              className="!h-[24px] !w-[24px] !rounded-full !bg-[#F0F7FF] !p-0"
              onClick={onClose}
            >
              <IconX width={15} />
            </Button>
          </Tooltip>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-6">{children}</div>
    </aside>
  )
}
